import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CosmicField from "@/components/CosmicField";
import HorizonRing from "@/components/HorizonRing";
import LevelUpModal from "@/components/LevelUpModal";
import { BRANCH_META } from "@/lib/branches";
import {
    Compass,
    PenTool,
    Terminal,
    Palette,
    BarChart3,
    Hammer,
    Eye,
    HandHeart,
    ArrowRight,
    Flame,
    CheckCircle2,
} from "lucide-react";

const ICONS = { PenTool, Terminal, Palette, BarChart3, Hammer };

export default function Onboarding() {
    const nav = useNavigate();
    const { user, refresh } = useAuth();
    const [step, setStep] = useState(1);
    const [cards, setCards] = useState([]);
    const [picked, setPicked] = useState([]);
    const [disciplines, setDisciplines] = useState([]);
    const [frontier, setFrontier] = useState(null);
    const [mode, setMode] = useState(null); // observer | contributor
    const [reward, setReward] = useState(null);
    const [microquestDone, setMicroquestDone] = useState(false);
    const [microFix, setMicroFix] = useState("");
    const [busy, setBusy] = useState(false);
    // Final handoff card: shown after the LevelUp modal closes so the very
    // last thing a new signup sees is their exemplar organization + role,
    // not a raw discipline canvas. Fetched lazily so it doesn't slow down
    // the microquest step.
    const [handoff, setHandoff] = useState(null);
    const [handoffOpen, setHandoffOpen] = useState(false);

    useEffect(() => {
        api.get("/atlas/onboarding-cards").then((r) => setCards(r.data));
    }, []);

    // Once user picks horizon cards, load 6 recommended disciplines.
    useEffect(() => {
        if (step !== 3 || picked.length === 0) return;
        const branchSet = new Set();
        picked.forEach((p) => {
            const card = cards.find((c) => c.id === p);
            card?.branches?.forEach((b) => branchSet.add(b));
        });
        const targets = Array.from(branchSet);
        Promise.all(
            targets.map((b) => api.get(`/atlas/disciplines?branch=${b}`).then((r) => r.data))
        ).then((groups) => {
            const flat = groups.flat();
            // sort by lowest progress (most opportunity) then take 6
            const sorted = [...flat].sort((a, b) => a.progress - b.progress).slice(0, 6);
            setDisciplines(sorted);
        });
    }, [step, picked, cards]);

    const togglePick = (id) => {
        setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
    };

    const igniteBeacon = async () => {
        if (!frontier || !mode) return;
        setBusy(true);
        try {
            const { data } = await api.post("/onboarding/complete", {
                horizons: picked,
                frontier_slug: frontier.slug,
                mode,
            });
            setReward(data.reward);
            await refresh();
            setStep(5);
        } finally {
            setBusy(false);
        }
    };

    const completeMicroquest = async () => {
        if (!microFix.trim()) return;
        setBusy(true);
        try {
            const { data } = await api.post("/onboarding/microquest", {
                discipline_slug: frontier.slug,
                fix: microFix,
            });
            setMicroquestDone(true);
            setReward(data.reward);
            await refresh();
        } finally {
            setBusy(false);
        }
    };

    const finish = () => {
        setReward(null);
        nav(`/discipline/${frontier?.slug || ""}`);
    };

    // Open the handoff card when the LevelUp modal is dismissed after the
    // microquest completes. If the fetch fails, we skip straight to
    // finish() so a broken LLM key never traps the user in onboarding.
    const openHandoff = async () => {
        if (!frontier?.slug) return finish();
        try {
            const { data } = await api.get(`/disciplines/${frontier.slug}/career-path`);
            setHandoff(data);
            setHandoffOpen(true);
        } catch {
            finish();
        }
    };

    return (
        <div className="min-h-screen w-full relative overflow-hidden">
            <div className="absolute inset-0 cosmic-sky" style={{ opacity: step === 1 ? 1 : 0, transition: "opacity 700ms" }} />
            {step === 1 && <CosmicField count={70} />}

            <div className="relative z-10 min-h-screen flex items-center justify-center px-6 py-16">
                {/* Progress dots */}
                <div className="fixed top-6 left-1/2 -translate-x-1/2 flex gap-2 z-30">
                    {[1, 2, 3, 4, 5].map((i) => (
                        <div
                            key={i}
                            className="h-1.5 w-8 rounded-full transition-all"
                            style={{
                                background: i <= step ? "#0F172A" : "rgba(255,255,255,0.4)",
                                boxShadow: i === step ? "0 0 8px rgba(255,255,255,0.6)" : "none",
                            }}
                            data-testid={`onboarding-step-${i}`}
                        />
                    ))}
                </div>

                <AnimatePresence mode="wait">
                    {step === 1 && (
                        <motion.div
                            key="1"
                            initial={{ opacity: 0, scale: 0.94 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 1.05 }}
                            className="text-center max-w-3xl"
                        >
                            <p
                                className="chip mx-auto mb-6"
                                style={{ background: "rgba(255,255,255,0.3)", color: "#0A2540" }}
                            >
                                Step 1 · Cosmic Welcome
                            </p>
                            <h1
                                className="font-display font-black text-5xl sm:text-6xl tracking-tighter text-white leading-tight"
                                style={{ textShadow: "0 4px 40px rgba(10,37,64,0.4)" }}
                            >
                                You are standing at the entrance
                                <br />
                                to <span className="text-arts">every discipline</span>.
                            </h1>
                            <p className="text-white/90 text-lg mt-6 max-w-xl mx-auto">
                                Zoom out and the map looks like a single web. Zoom in and it becomes a
                                galaxy of specific work. Ready to find your first neighborhood?
                            </p>
                            <button
                                onClick={() => setStep(2)}
                                className="arcade-btn mt-10"
                                data-variant="arts"
                                data-testid="onboarding-continue-1"
                            >
                                Pinch to zoom in <ArrowRight className="w-4 h-4" />
                            </button>
                        </motion.div>
                    )}

                    {step === 2 && (
                        <motion.div
                            key="2"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="w-full max-w-3xl"
                        >
                            <p className="chip mb-4" style={{ background: BRANCH_META.humanities.soft, color: BRANCH_META.humanities.text }}>
                                Step 2 · Horizon Selection
                            </p>
                            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-slate-900 leading-tight">
                                Which paths make you curious?
                            </h2>
                            <p className="text-slate-600 mt-2 mb-8">
                                Tap two or three. Don't overthink it — this only lights up part of the
                                map, not your whole life.
                            </p>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {cards.map((c) => {
                                    const Icon = ICONS[c.icon] || Compass;
                                    const active = picked.includes(c.id);
                                    const color = BRANCH_META[c.branches?.[0] || "sciences"];
                                    return (
                                        <button
                                            key={c.id}
                                            onClick={() => togglePick(c.id)}
                                            className={`crystal rounded-2xl p-5 text-left transition-all relative overflow-hidden ${
                                                active ? "scale-[1.02]" : "hover:scale-[1.01]"
                                            }`}
                                            style={{
                                                borderColor: active ? color.color : undefined,
                                                boxShadow: active ? `0 8px 24px -8px ${color.color}` : undefined,
                                            }}
                                            data-testid={`horizon-card-${c.id}`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div
                                                    className="w-11 h-11 rounded-xl flex items-center justify-center"
                                                    style={{ background: color.color }}
                                                >
                                                    <Icon className="w-5 h-5" color={c.branches?.[0] === "arts" ? "#0F172A" : "#fff"} />
                                                </div>
                                                <div>
                                                    <div className="font-display font-bold text-lg text-slate-900 tracking-tight">
                                                        {c.label}
                                                    </div>
                                                    <div className="text-xs uppercase tracking-widest font-bold" style={{ color: color.text }}>
                                                        {c.branches.map((b) => BRANCH_META[b].name).join(" · ")}
                                                    </div>
                                                </div>
                                            </div>
                                            {active && (
                                                <CheckCircle2
                                                    className="w-5 h-5 absolute top-3 right-3"
                                                    color={color.color}
                                                />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="mt-8 flex items-center justify-between">
                                <button
                                    onClick={() => setStep(1)}
                                    className="text-slate-500 hover:text-slate-900 text-sm"
                                    data-testid="onboarding-back-2"
                                >
                                    ← Back
                                </button>
                                <button
                                    disabled={picked.length === 0}
                                    onClick={() => setStep(3)}
                                    className="arcade-btn"
                                    data-variant="primary"
                                    data-testid="onboarding-continue-2"
                                >
                                    Show my frontier <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {step === 3 && (
                        <motion.div
                            key="3"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="w-full max-w-4xl"
                        >
                            <p className="chip mb-4" style={{ background: BRANCH_META.sciences.soft, color: BRANCH_META.sciences.text }}>
                                Step 3 · Find your frontier
                            </p>
                            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-slate-900 leading-tight">
                                Six neighborhoods where you'd be useful today.
                            </h2>
                            <p className="text-slate-600 mt-2 mb-8">
                                Pick one to enter its workshop hub. You can join more later — this is
                                just where your ledger starts.
                            </p>

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {disciplines.map((d) => {
                                    const meta = BRANCH_META[d.branch];
                                    const active = frontier?.slug === d.slug;
                                    return (
                                        <button
                                            key={d.slug}
                                            onClick={() => setFrontier(d)}
                                            className="crystal rounded-2xl p-5 text-left transition-all"
                                            style={{
                                                borderColor: active ? meta.color : undefined,
                                                boxShadow: active
                                                    ? `0 12px 30px -12px ${meta.color}`
                                                    : undefined,
                                                transform: active ? "scale(1.02)" : undefined,
                                            }}
                                            data-testid={`frontier-${d.slug}`}
                                        >
                                            <div className="flex items-start justify-between">
                                                <span
                                                    className="chip"
                                                    style={{ background: meta.soft, color: meta.text }}
                                                >
                                                    {meta.name}
                                                </span>
                                                <HorizonRing size={54} stroke={6} pct={d.progress} color={meta.color} />
                                            </div>
                                            <h3 className="font-display font-bold text-xl text-slate-900 mt-3 tracking-tight">
                                                {d.name}
                                            </h3>
                                            <p className="text-sm text-slate-600 mt-1">{d.blurb}</p>
                                            <div className="text-xs mt-3 text-slate-500">
                                                {d.open_tasks} open beginner tasks
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="mt-8 flex items-center justify-between">
                                <button
                                    onClick={() => setStep(2)}
                                    className="text-slate-500 hover:text-slate-900 text-sm"
                                    data-testid="onboarding-back-3"
                                >
                                    ← Back
                                </button>
                                <button
                                    disabled={!frontier}
                                    onClick={() => setStep(4)}
                                    className="arcade-btn"
                                    data-variant="primary"
                                    data-testid="onboarding-continue-3"
                                >
                                    Enter {frontier ? frontier.name : "the hub"} <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {step === 4 && frontier && (
                        <motion.div
                            key="4"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="w-full max-w-2xl"
                        >
                            <p
                                className="chip mb-4"
                                style={{ background: BRANCH_META[frontier.branch].soft, color: BRANCH_META[frontier.branch].text }}
                            >
                                Step 4 · Ignite your beacon
                            </p>
                            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-slate-900 leading-tight">
                                How do you want to show up today?
                            </h2>
                            <p className="text-slate-600 mt-2 mb-6">
                                You're about to join the global roster for{" "}
                                <span className="font-bold" style={{ color: BRANCH_META[frontier.branch].color }}>
                                    {frontier.name}
                                </span>
                                . Pick a mode — you can toggle it any time.
                            </p>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <button
                                    onClick={() => setMode("observer")}
                                    className="crystal rounded-2xl p-6 text-left transition-all"
                                    style={{
                                        borderColor: mode === "observer" ? BRANCH_META[frontier.branch].color : undefined,
                                        transform: mode === "observer" ? "scale(1.02)" : undefined,
                                    }}
                                    data-testid="mode-observer"
                                >
                                    <Eye className="w-6 h-6" color={BRANCH_META[frontier.branch].color} />
                                    <div className="font-display font-bold text-lg text-slate-900 mt-2">Observer</div>
                                    <div className="text-sm text-slate-600 mt-1">
                                        I want to lurk, read research, and learn the landscape.
                                    </div>
                                </button>
                                <button
                                    onClick={() => setMode("contributor")}
                                    className="crystal rounded-2xl p-6 text-left transition-all"
                                    style={{
                                        borderColor: mode === "contributor" ? BRANCH_META[frontier.branch].color : undefined,
                                        transform: mode === "contributor" ? "scale(1.02)" : undefined,
                                    }}
                                    data-testid="mode-contributor"
                                >
                                    <HandHeart className="w-6 h-6" color={BRANCH_META[frontier.branch].color} />
                                    <div className="font-display font-bold text-lg text-slate-900 mt-2">Contributor</div>
                                    <div className="text-sm text-slate-600 mt-1">
                                        I want to browse open micro-tasks and help build right now.
                                    </div>
                                </button>
                            </div>

                            <div className="mt-8 flex items-center justify-between">
                                <button
                                    onClick={() => setStep(3)}
                                    className="text-slate-500 hover:text-slate-900 text-sm"
                                    data-testid="onboarding-back-4"
                                >
                                    ← Back
                                </button>
                                <button
                                    disabled={!mode || busy}
                                    onClick={igniteBeacon}
                                    className="arcade-btn"
                                    data-variant={frontier.branch}
                                    data-testid="ignite-beacon"
                                >
                                    <Flame className="w-4 h-4" /> Ignite beacon
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {step === 5 && frontier && (
                        <motion.div
                            key="5"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            className="w-full max-w-2xl"
                        >
                            <p
                                className="chip mb-4"
                                style={{ background: BRANCH_META[frontier.branch].soft, color: BRANCH_META[frontier.branch].text }}
                            >
                                Step 5 · Your first micro-quest
                            </p>
                            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-slate-900 leading-tight">
                                Break the ice in {frontier.name}.
                            </h2>
                            <p className="text-slate-600 mt-2 mb-6">
                                Read the one-page orientation below. Spot a typo, a broken link, or a
                                confusing sentence — suggest an edit and your permanent verified
                                resume ledger goes live.
                            </p>

                            <div
                                className="crystal rounded-2xl p-6 font-mono text-sm text-slate-700 leading-relaxed"
                                data-testid="microquest-doc"
                            >
                                <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">
                                    {frontier.name} · Orientation v1.0
                                </div>
                                <p className="mb-2">
                                    Welcome to the {frontier.name} workshop. This space is a shared
                                    workshop floor — anyone can watch, and anyone with a beacon can
                                    contribute.
                                </p>
                                <p className="mb-2">
                                    The current frontier: <b>{frontier.blurb}</b>
                                </p>
                                <p className="mb-2">
                                    Community progress: {frontier.progress}% mapped. Open micro-tasks:{" "}
                                    {frontier.open_tasks}. See a sentence you'd word better? Tell us
                                    below — even a single-character fix counts.
                                </p>
                            </div>

                            <textarea
                                value={microFix}
                                onChange={(e) => setMicroFix(e.target.value)}
                                placeholder="Your suggested edit — e.g. 'micro-tasks' should be one word here."
                                className="field-input mt-4 min-h-[100px] resize-y"
                                data-testid="microquest-input"
                            />

                            <div className="mt-6 flex items-center justify-between">
                                <button
                                    onClick={finish}
                                    className="text-slate-500 hover:text-slate-900 text-sm"
                                    data-testid="skip-microquest"
                                >
                                    Skip for now
                                </button>
                                <button
                                    disabled={!microFix.trim() || busy}
                                    onClick={completeMicroquest}
                                    className="arcade-btn"
                                    data-variant="primary"
                                    data-testid="submit-microquest"
                                >
                                    Submit & launch ledger <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            <LevelUpModal reward={reward} onClose={() => (microquestDone ? openHandoff() : setReward(null))} />

            {/* Career-path handoff — the last thing a new signup sees. Reads
                like a welcome from the exemplar org for their frontier
                discipline. Non-blocking: they can skip straight to the map. */}
            {handoffOpen && handoff && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-md p-4" data-testid="onboarding-handoff">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="crystal rounded-3xl w-full max-w-lg overflow-hidden"
                        style={{
                            borderTop: `4px solid ${BRANCH_META[handoff.path?.branch || frontier?.branch]?.color || "#0F172A"}`,
                        }}
                    >
                        <div className="p-6 sm:p-8">
                            <div className="flex items-start gap-3">
                                <div
                                    className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 font-display font-black text-white text-lg"
                                    style={{
                                        background: BRANCH_META[handoff.path?.branch || frontier?.branch]?.color || "#0F172A",
                                    }}
                                >
                                    {(handoff.path?.org_name || "?")
                                        .split(" ").slice(0, 2).map((w) => w[0]).join("")}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: BRANCH_META[handoff.path?.branch || frontier?.branch]?.text || "#0F172A" }}>
                                        Meet your first career guide
                                    </div>
                                    <div className="font-display font-black text-2xl tracking-tighter text-slate-900 leading-tight" data-testid="onboarding-handoff-org">
                                        {handoff.path?.org_name}
                                    </div>
                                    <div className="text-sm text-slate-700 mt-0.5">
                                        Track: <span className="font-bold">{handoff.path?.role_title}</span>
                                    </div>
                                </div>
                            </div>
                            {handoff.path?.intro && (
                                <p className="text-sm text-slate-800 mt-4 italic border-l-2 pl-3" style={{ borderColor: BRANCH_META[handoff.path?.branch || frontier?.branch]?.color || "#0F172A" }}>
                                    {handoff.path.intro}
                                </p>
                            )}
                            {handoff.path?.mission && (
                                <p className="text-sm text-slate-600 mt-3 leading-relaxed">
                                    {handoff.path.mission}
                                </p>
                            )}
                            <div className="mt-6 flex flex-wrap gap-2 justify-end">
                                <button
                                    onClick={finish}
                                    className="text-sm text-slate-500 hover:text-slate-900 font-display font-bold"
                                    data-testid="onboarding-handoff-skip"
                                >
                                    Skip
                                </button>
                                <button
                                    onClick={finish}
                                    className="arcade-btn"
                                    data-variant="primary"
                                    data-testid="onboarding-handoff-continue"
                                >
                                    See the roadmap <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
}
